import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Layout";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { getCapturedReferralCode, clearCapturedReferralCode } from "@/components/ReferralCapture";

const Wrap = ({children, title, subtitle, testid}) => (
  <div className="min-h-screen bg-slate-50 flex flex-col" data-testid={testid}>
    <div className="max-w-md mx-auto w-full pt-12 px-6"><Logo/></div>
    <div className="max-w-md mx-auto w-full px-6 pt-8 pb-16 flex-1">
      <h1 className="text-3xl font-black font-display text-slate-900">{title}</h1>
      {subtitle && <p className="mt-2 text-slate-600">{subtitle}</p>}
      <div className="mt-6 bg-white border border-slate-200 rounded-md p-8">{children}</div>
    </div>
  </div>
);

const Input = ({label, ...rest}) => (
  <label className="block">
    <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
    <input className="w-full border border-slate-300 rounded-md px-3 py-2 focus:border-red-500 focus:outline-none" {...rest}/>
  </label>
);

const PasswordInput = ({label, ...rest}) => {
  const [show, setShow] = React.useState(false);
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      <div className="relative">
        <input className="w-full border border-slate-300 rounded-md px-3 py-2 pr-11 focus:border-red-500 focus:outline-none"
               type={show ? "text" : "password"} {...rest}/>
        <button type="button" onClick={() => setShow(!show)} tabIndex={-1}
                aria-label={show ? "Hide password" : "Show password"}
                data-testid="toggle-password-visibility"
                className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700">
          {show ? <EyeOff className="h-5 w-5"/> : <Eye className="h-5 w-5"/>}
        </button>
      </div>
    </label>
  );
};

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [f, setF] = React.useState({email:"", password:""});
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async (e)=>{
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const u = await login(f.email, f.password);
      toast.success("Welcome back!");
      nav(u.role==='admin'?'/admin':u.role==='steward'?'/steward-dashboard':'/dashboard');
    } catch (err) { setError(err.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };
  return (
    <Wrap testid="page-login" title="Welcome back" subtitle="Sign in to your account.">
      <form onSubmit={submit} className="space-y-4">
        <Input data-testid="login-email" label="Email" type="email" required value={f.email} onChange={e=>setF({...f, email:e.target.value})}/>
        <PasswordInput data-testid="login-password" label="Password" required value={f.password} onChange={e=>setF({...f, password:e.target.value})}/>
        {error && <p data-testid="login-error" className="text-red-600 text-sm">{error}</p>}
        <Button data-testid="login-submit" disabled={busy} type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white h-11">{busy?"Signing in...":"Sign in"}</Button>
      </form>
      <div className="mt-4 text-sm flex justify-between">
        <Link to="/forgot" className="text-slate-600 hover:text-slate-900" data-testid="login-forgot-link">Forgot password?</Link>
        <Link to="/register" className="text-red-600 font-semibold" data-testid="login-register-link">Create account</Link>
      </div>
    </Wrap>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = React.useState({first_name:"",last_name:"",email:"",password:"",confirm:"",agree:false,become_advocate:false});
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async (e)=>{
    e.preventDefault(); setError("");
    if (f.password !== f.confirm) return setError("Passwords do not match");
    if (f.password.length < 8) return setError("Password must be at least 8 characters");
    if (!f.agree) return setError("You must agree to the Terms and Privacy Policy");
    setBusy(true);
    try {
      const referral_code = getCapturedReferralCode();
      await register({first_name:f.first_name, last_name:f.last_name, email:f.email, password:f.password, agree_terms:true, become_advocate: f.become_advocate, ...(referral_code ? { referral_code } : {})});
      clearCapturedReferralCode();
      toast.success("Account created!");
      nav("/profile");
    } catch (err) { setError(err.response?.data?.detail || "Registration failed"); }
    finally { setBusy(false); }
  };
  return (
    <Wrap testid="page-register" title="Create your account" subtitle="Start your first grievance in minutes.">
      {!!getCapturedReferralCode() && (
        <div className="mb-4 bg-red-50 border border-red-100 rounded-md px-4 py-3 text-sm" data-testid="register-referral-note">
          <p className="font-semibold text-slate-900">You were invited by a fellow UPSer.</p>
          <p className="text-slate-700">Complete your free account setup below.</p>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="reg-first" label="First name" required value={f.first_name} onChange={e=>setF({...f, first_name:e.target.value})}/>
          <Input data-testid="reg-last" label="Last name" required value={f.last_name} onChange={e=>setF({...f, last_name:e.target.value})}/>
        </div>
        <Input data-testid="reg-email" label="Email" type="email" required value={f.email} onChange={e=>setF({...f, email:e.target.value})}/>
        <PasswordInput data-testid="reg-password" label="Password (min 8 chars)" required value={f.password} onChange={e=>setF({...f, password:e.target.value})}/>
        <PasswordInput data-testid="reg-confirm" label="Confirm password" required value={f.confirm} onChange={e=>setF({...f, confirm:e.target.value})}/>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input data-testid="reg-agree" type="checkbox" checked={f.agree} onChange={e=>setF({...f, agree:e.target.checked})} className="mt-1"/>
          <span>I agree to the <Link to="/terms" className="text-red-600 underline">Terms of Use</Link> and <Link to="/privacy" className="text-red-600 underline">Privacy Policy</Link>.</span>
        </label>
        {/* Optional Advocate Program opt-in — Build 1. Visually secondary; never blocks account creation. */}
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="reg-advocate-block">
          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input data-testid="reg-become-advocate" type="checkbox" checked={f.become_advocate} onChange={e=>setF({...f, become_advocate:e.target.checked})} className="mt-1"/>
            <span>
              <span className="font-semibold text-slate-900">Become an Advocate</span> <span className="text-xs text-slate-500 uppercase tracking-widest">Optional</span>
              <span className="block text-xs text-slate-600 mt-0.5 leading-relaxed">
                Help introduce FileMyGrievance to other UPS Teamsters. You can participate in the FMG Advocate Program — reviewed and approved separately by our team. Optional and never required.
              </span>
            </span>
          </label>
        </div>
        {error && <p data-testid="reg-error" className="text-red-600 text-sm">{error}</p>}
        <Button data-testid="reg-submit" disabled={busy} type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white h-11">{busy?"Creating...":"Create account"}</Button>
      </form>
      <div className="mt-4 text-sm text-center">
        <Link to="/login" className="text-slate-600 hover:text-slate-900" data-testid="reg-login-link">Already have an account? Sign in</Link>
      </div>
    </Wrap>
  );
}

export function Forgot() {
  const [email, setEmail] = React.useState("");
  const [msg, setMsg] = React.useState("");
  const [demo, setDemo] = React.useState("");
  const submit = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/auth/forgot", { email });
    setMsg(data.message);
    if (data.demo_token) setDemo(data.demo_token);
  };
  return (
    <Wrap testid="page-forgot" title="Reset your password" subtitle="Enter your email — we'll send a reset link.">
      <form onSubmit={submit} className="space-y-4">
        <Input data-testid="forgot-email" label="Email" type="email" required value={email} onChange={e=>setEmail(e.target.value)}/>
        <Button data-testid="forgot-submit" type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white h-11">Send reset link</Button>
      </form>
      {msg && <div className="mt-4 text-sm text-slate-600">{msg}</div>}
      {demo && (
        <div className="mt-3 text-xs bg-amber-50 border border-amber-200 p-3 rounded" data-testid="forgot-demo-token">
          <p className="font-semibold">Demo mode:</p>
          <p>Use this token on the reset page:</p>
          <code className="break-all">{demo}</code>
          <Link to={`/reset?token=${demo}`} className="block mt-2 text-red-600 underline">Go to reset page</Link>
        </div>
      )}
    </Wrap>
  );
}

export function Reset() {
  const params = new URLSearchParams(window.location.search);
  const [token, setToken] = React.useState(params.get("token")||"");
  const [password, setPassword] = React.useState("");
  const [msg, setMsg] = React.useState("");
  const [error, setError] = React.useState("");
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setError(""); setMsg("");
    try {
      await api.post("/auth/reset", { token, password });
      setMsg("Password reset! Redirecting to login...");
      setTimeout(()=>nav("/login"), 1500);
    } catch (err) { setError(err.response?.data?.detail || "Reset failed"); }
  };
  return (
    <Wrap testid="page-reset" title="Set a new password">
      <form onSubmit={submit} className="space-y-4">
        <Input data-testid="reset-token" label="Reset token" required value={token} onChange={e=>setToken(e.target.value)}/>
        <Input data-testid="reset-password" label="New password" type="password" required value={password} onChange={e=>setPassword(e.target.value)}/>
        {error && <p className="text-red-600 text-sm">{error}</p>}
        {msg && <p className="text-green-700 text-sm">{msg}</p>}
        <Button data-testid="reset-submit" type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white h-11">Reset password</Button>
      </form>
    </Wrap>
  );
}
